<template>
  <div v-if="showPagination" class="pagination-container">
    <div class="pagination-info">
      <span class="pagination-text">
        {{ t('events.pagination.showing') }} {{ startItem === endItem ? startItem : startItem + '-' + endItem }} {{ t('events.pagination.of') }} {{ totalCount }} {{ totalCount === 1 ? t('events.pagination.event_singular') : t('events.pagination.event_plural') }}
      </span>
    </div>

    <div class="pagination-controls">
      <!-- Previous button -->
      <button
        @click="goToPage(currentPage - 1)"
        :disabled="!hasPrevPage"
        class="pagination-btn pagination-btn-prev"
        :class="{ disabled: !hasPrevPage }"
      >
        <i class="bi bi-chevron-left"></i>
        <span class="btn-text">{{ t('events.pagination.prev') }}</span>
      </button>

      <!-- Page numbers -->
      <div class="page-numbers">
        <!-- First page -->
        <button
          v-if="showFirstPage"
          @click="goToPage(1)"
          class="pagination-btn page-number"
          :class="{ active: currentPage === 1 }"
        >
          1
        </button>

        <!-- Ellipsis after first page -->
        <span v-if="showFirstEllipsis" class="pagination-ellipsis">...</span>

        <!-- Page numbers around current page -->
        <button
          v-for="page in visiblePages"
          :key="page"
          @click="goToPage(page)"
          class="pagination-btn page-number"
          :class="{ active: currentPage === page }"
        >
          {{ page }}
        </button>

        <!-- Ellipsis before last page -->
        <span v-if="showLastEllipsis" class="pagination-ellipsis">...</span>

        <!-- Last page -->
        <button
          v-if="showLastPage"
          @click="goToPage(totalPages)"
          class="pagination-btn page-number"
          :class="{ active: currentPage === totalPages }"
        >
          {{ totalPages }}
        </button>
      </div>

      <!-- Next button -->
      <button
        @click="goToPage(currentPage + 1)"
        :disabled="!hasNextPage"
        class="pagination-btn pagination-btn-next"
        :class="{ disabled: !hasNextPage }"
      >
        <span class="btn-text">{{ t('events.pagination.next') }}</span>
        <i class="bi bi-chevron-right"></i>
      </button>
    </div>

    <!-- Per page selector -->
    <div class="per-page-selector">
      <label>
        {{ t('events.pagination.per_page') }}
        <AppSelect
          v-model="localPerPage"
          variant="filter"
          class="per-page-app-select"
          :options="perPageOptions"
          @change="onPerPageChange"
        />
      </label>
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import AppSelect from '../../shared/AppSelect.vue';

export default {
  name: 'Pagination',
  components: { AppSelect },
  setup() {
    const i18n = useI18n();
    return {
      t: i18n.t
    };
  },
  props: {
    currentPage: {
      type: Number,
      required: true
    },
    totalPages: {
      type: Number,
      required: true
    },
    totalCount: {
      type: Number,
      required: true
    },
    perPage: {
      type: Number,
      required: true
    }
  },
  emits: ['page-change', 'per-page-change'],
  data() {
    return {
      localPerPage: this.perPage
    }
  },
  computed: {
    showPagination() {
      return this.totalPages > 1 || this.totalCount > 0;
    },
    hasPrevPage() {
      return this.currentPage > 1;
    },
    hasNextPage() {
      return this.currentPage < this.totalPages;
    },
    startItem() {
      return ((this.currentPage - 1) * this.perPage) + 1;
    },
    endItem() {
      const end = this.currentPage * this.perPage;
      return Math.min(end, this.totalCount);
    },
    showFirstPage() {
      return this.currentPage > 3;
    },
    showLastPage() {
      return this.currentPage < this.totalPages - 2;
    },
    showFirstEllipsis() {
      return this.currentPage > 4;
    },
    showLastEllipsis() {
      return this.currentPage < this.totalPages - 3;
    },
    visiblePages() {
      const pages = [];
      const start = Math.max(2, this.currentPage - 1);
      const end = Math.min(this.totalPages - 1, this.currentPage + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    },
    perPageOptions() {
      return [
        { value: 10, label: '10' },
        { value: 20, label: '20' },
        { value: 50, label: '50' },
      ];
    },
  },
  methods: {
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.$emit('page-change', page);
      }
    },
    onPerPageChange() {
      this.$emit('per-page-change', this.localPerPage);
    }
  },
  watch: {
    perPage(newValue) {
      this.localPerPage = newValue;
    }
  }
}
</script>

<style lang="scss" scoped>
// Variables
$primary-color: #bd9c65;
$primary-hover: #c29c58;
$primary-light: #efd8b0;
$text-color: #60451f;
$border-color: #dee2e6;
$background-light: #f8f9fa;
$text-muted: #6c757d;
$disabled-color: #999;
$disabled-bg: #f5f5f5;
$white: white;

// Mixins
@mixin button-base {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  border: 1px solid $primary-color;
  border-radius: 4px;
  background-color: $white;
  color: $text-color;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
  justify-content: center;
}

@mixin button-hover {
  background-color: $primary-light;
  border-color: $primary-hover;
  color: $text-color;
}

@mixin button-active {
  background-color: $primary-hover;
  border-color: $primary-hover;
  color: $white;
  font-weight: bold;
}

@mixin button-disabled {
  background-color: $disabled-bg;
  border-color: $border-color;
  color: $disabled-color;
  cursor: not-allowed;
}

.pagination-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  margin: 30px 0;
  padding: 20px;
  background-color: $background-light;
  border: 1px solid $border-color;
  border-radius: 8px;

  .pagination-info {
    text-align: center;

    .pagination-text {
      color: $text-muted;
      font-size: 14px;
      font-weight: 500;
    }
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: center;

    .pagination-btn {
      @include button-base;

      &:hover:not(.disabled) {
        @include button-hover;
      }

      &.active {
        @include button-active;
      }

      &.disabled {
        @include button-disabled;
      }

      &-prev,
      &-next {
        min-width: 80px;
      }
    }

    .btn-text {
      display: inline-block;
    }

    .page-numbers {
      display: flex;
      align-items: center;
      gap: 5px;

      .page-number {
        min-width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    .pagination-ellipsis {
      color: $text-muted;
      font-weight: bold;
      padding: 0 5px;
    }
  }

  .per-page-selector {
    display: flex;
    align-items: center;
    gap: 10px;

    label {
      display: flex;
      align-items: center;
      gap: 8px;
      color: $text-color;
      font-size: 14px;
      font-weight: 500;
    }

    .per-page-select {
      padding: 6px 10px;
      border: 1px solid $primary-color;
      border-radius: 4px;
      background-color: $white;
      color: $text-color;
      font-size: 14px;
      cursor: pointer;

      &:focus {
        outline: none;
        border-color: $primary-hover;
        box-shadow: 0 0 0 2px rgba($primary-hover, 0.2);
      }
    }
  }

  // Mobile responsive styles
  @media (max-width: 768px) {
    .pagination-controls {
      flex-direction: column;
      gap: 15px;

      .page-numbers {
        order: 2;
      }

      .pagination-btn {
        &-prev {
          order: 1;
          min-width: 120px;
        }

        &-next {
          order: 3;
          min-width: 120px;
        }
      }
    }

    .per-page-selector {
      order: 4;
    }
  }
}
</style>
